import { Component, ReactNode } from "react";
import classNames from "classnames";

interface ErrorBoundaryState {
  hasError: boolean;
}

class ModuleErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Module error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

export interface ModuleSectionProps {
  preHeader?: string;
  title?: string;
  subtitle?: string;
  children?: React.ReactNode;
  className?: string;
  sectionDivider?: boolean;
}

const SectionDivider = () => (
  <div className="mt-[80px] xs:mt-[180px] xs:w-[1014px] w-[300px] h-px shrink-0 bg-[radial-gradient(50%_50%_at_50%_50%,#F4C874_0%,rgba(244,200,116,0.00)_100%)] shadow-[0_2px_0_0_rgba(0,0,0,0.40)] mx-auto" />
);

export const ModuleSection: React.FC<ModuleSectionProps> = ({
  preHeader,
  title,
  subtitle,
  children,
  className = "",
  sectionDivider = true,
}) => {
  return (
    <section className={classNames("flex flex-col items-center mt-[80px] xs:mt-[180px]", className)}>
      <div className="max-w-4xl mx-auto px-4 text-center">
        {preHeader && (
          <p className="text-white font-barlow font-semibold xs:text-[20px] text-[16px] uppercase leading-none">{preHeader}</p>
        )}

        <h2 className="text-[#F4C874] font-barlow font-semibold xs:text-[48px] text-[36px] uppercase leading-tight mt-2">
          {title}
        </h2>

        {subtitle && (
          <p className="text-[#CDDCFE] font-inter xs:text-[18px] text-[14px] font-normal leading-relaxed xs:max-w-[496px] max-w-[360px] mx-auto mt-6">
            {subtitle}
          </p>
        )}
      </div>

      {children && <div className="flex justify-center mt-[48px] w-full">{children}</div>}
      {sectionDivider && <SectionDivider />}
    </section>
  );
};

export const SafeModuleSection: React.FC<ModuleSectionProps> = (props) => {
  return (
    <ModuleErrorBoundary>
      <ModuleSection {...props} />
    </ModuleErrorBoundary>
  );
};
